import { usePathStateStore } from '@client/stores/PathStateStore';
import { isSafeToSilentUpdate, performSilentUpdate } from '@client/utils/version/silent-update';
import type { AnyRouter } from '@tanstack/react-router';

/**
 * Wire the per-navigation side effects onto the router: prune the path state of the pages being
 * left (so a list page re-entered from another section starts unfiltered), and take a pending
 * silent update at the one moment the app is already discarding its screen.
 *
 * Subscribes to `onBeforeLoad`, NOT `onBeforeNavigate`. The router emits `onBeforeNavigate` only
 * while its internal `redirect` store is empty, and that store is written ONLY inside `load()`'s
 * catch — so the first `throw redirect()` of a tab session (any index route: workload-manager,
 * compliance, controls, a complaint detail…) leaves it set forever and mutes the event for good.
 * Everything wired to it then dies silently mid-session: cleanup stops running and every list
 * page's filters and query params survive cross-section navigation. `onBeforeLoad` carries the
 * identical payload, fires immediately after, and is emitted unconditionally. `preloadRoute` never
 * emits it, so hover-preloading a link still cannot prune state.
 *
 * @returns the router's unsubscribe function.
 */
export function trackNavigationSideEffects(router: AnyRouter): () => void {
    return router.subscribe('onBeforeLoad', (event) => {
        if (event.toLocation?.pathname) {
            usePathStateStore.getState().cleanupStatesForNavigation(event.toLocation.pathname);
        }

        if (event.toLocation && isSafeToSilentUpdate(event.toLocation, router, { pathChanged: event.pathChanged })) {
            performSilentUpdate(router.getParsedLocationHref(event.toLocation));
        }
    });
}
